import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import { useMergedRef } from "@src/global_functions/hooks";
import { PORTAL_ROOT_ID } from "@src/PortalRoot";
import classNames from "classnames";
import { useTransitionState } from "react-transition-state";
import { SubscribeFn } from "../../../global_functions/hooks/useSubscription";
import CollapsibleContainer, {
  ANIMATION_DURATION,
} from "../CollapsibleContainer/CollapsibleContainer";

type TransitionStatus =
  | "preEnter"
  | "entering"
  | "entered"
  | "preExit"
  | "exiting"
  | "exited"
  | "unmounted";

type DropdownWrapperProps = {
  children: React.ReactNode;
  as: React.ReactElement;
  disabled?: boolean;
  maxHeight?: number;
  fullWidth?: boolean;
  minWidth?: number;
  positionRelative?: boolean;
  /**
   * "positionRelative"
   * When true, no portal will be used and the dropdown will
   * render normally in the parent container expanding it if necessary.
   */
  positionRelativeHeight?: number;
  innerClassName?: string;
  allowRenderAbove?: boolean;
  extraRefs?: React.RefObject<HTMLElement>[];
  extraDropdownRef?: React.RefObject<HTMLDivElement>;
  // used to orient the dropdown to the ref element
  orientRef?: React.RefObject<HTMLElement>;
  closeSubscription?: SubscribeFn;

  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;

  onOpen?: () => void;
  onClose?: () => void;
  onTransitionEnd?: (newOpenState: boolean) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
};

const ESCAPE_KEYCODE = 27;

export default function DropdownWrapper(props: Readonly<DropdownWrapperProps>) {
  const [internalMenuOpened, setInternalMenuOpened] = React.useState(
    props.open ?? false
  );
  const menuOpened = props.open ?? internalMenuOpened;
  const setMenuOpened = props.setOpen ?? setInternalMenuOpened;

  const { onClose, onOpen } = props;

  useEffect(() => {
    if (menuOpened) {
      onOpen?.();
    } else {
      onClose?.();
    }
  }, [onOpen, onClose, menuOpened]);

  const containerRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const refs = useMemo(
    () => [containerRef, dropdownRef, ...(props.extraRefs ?? [])],
    [props.extraRefs]
  );

  useEffect(() => {
    if (menuOpened) {
      const onDocumentClick = (e: MouseEvent) => {
        if (
          refs.every(
            (ref) => ref.current && !ref.current.contains(e.target as Node)
          )
        ) {
          setMenuOpened(false);
          onClose?.();
        }
      };
      document.addEventListener("mousedown", onDocumentClick);
      return () => {
        document.removeEventListener("mousedown", onDocumentClick);
      };
    }
  }, [menuOpened, refs, onClose, setMenuOpened]);

  const innerRef = useRef<HTMLDivElement | null>(null);
  const mergedInnerRefs = useMergedRef(
    innerRef,
    ...(props.extraDropdownRef ? [props.extraDropdownRef] : [])
  );
  useEffect(() => {
    if (menuOpened && props.positionRelative) {
      const timeout = setTimeout(() => {
        innerRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, ANIMATION_DURATION);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [menuOpened, props.positionRelative]);

  useEffect(() => {
    const listener = (e: any) => {
      if (e.keyCode === ESCAPE_KEYCODE) setMenuOpened(false);
    };
    window.addEventListener("keyup", listener);
    return () => window.removeEventListener("keyup", listener);
  }, [setMenuOpened]);

  const { closeSubscription } = props;
  useEffect(
    () => closeSubscription?.(() => setMenuOpened(false)),
    [closeSubscription, setMenuOpened]
  );

  const root = document.getElementById(PORTAL_ROOT_ID);

  React.useEffect(() => {
    if (props.disabled) {
      setMenuOpened(false);
    }
  }, [props.disabled, setMenuOpened]);

  // Adding additional transitionState to prevent removing from DOM before onTransitionEnd is called
  // This way the Dropdown will be removed from DOM on the subsequent render
  const [hasExited, setHasExited] = React.useState(!menuOpened);
  const _onTransitionEnd = props.onTransitionEnd;
  const onTransitionEnd = React.useCallback(
    (newOpenState: boolean) => {
      if (!newOpenState) {
        setHasExited(true);
      } else {
        setHasExited(false);
      }
      _onTransitionEnd?.(newOpenState);
    },
    [_onTransitionEnd]
  );

  const [transitionState, toggleTransition] = useTransitionState({
    timeout: { enter: 0, exit: 250 },
    preEnter: true,
    initialEntered: menuOpened,
  });

  useEffect(() => {
    toggleTransition(menuOpened);
  }, [menuOpened, toggleTransition]);

  // force show on first render if open is true
  useEffect(() => {
    menuOpened && setInternalMenuOpened((prev) => !prev);
  }, []); // eslint-disable-line

  return (
    <>
      {React.cloneElement(props.as, {
        onClick: () => {
          if (!props.disabled) {
            setMenuOpened((prev) => !prev);
          }
        },
        onFocus: props.onFocus,
        onBlur: props.onBlur,
        ref: containerRef,
      })}
      {props.positionRelative ? (
        <CollapsibleContainer
          open={menuOpened}
          onTransitionEnd={onTransitionEnd}
        >
          <InnerContainer
            className={props.innerClassName}
            height={props.positionRelativeHeight}
            maxHeight={props.maxHeight}
            innerRef={mergedInnerRefs}
          >
            {props.children}
          </InnerContainer>
        </CollapsibleContainer>
      ) : (
        !(hasExited && transitionState.status === "exited") &&
        containerRef.current &&
        root &&
        createPortal(
          <Container
            transitionState={transitionState.status}
            fullWidth={props.fullWidth}
            minWidth={props.minWidth}
            maxHeight={props.maxHeight}
            allowRenderAbove={props.allowRenderAbove}
            containerRef={containerRef}
            dropdownRef={dropdownRef}
            extraDropdownRef={props.extraDropdownRef}
            innerClassName={props.innerClassName}
            onTransitionEnd={onTransitionEnd}
            orientRef={props.orientRef}
          >
            {props.children}
          </Container>,
          root
        )
      )}
    </>
  );
}

// dropdown menu fixed arbitrary values
const DROPDOWN_MENU_SPACE_BELOW_TO_SUBTRACT = 5;
const DROPDOWN_MENU_BOTTOM_BUFFER_HEIGHT = 30;
const DROPDOWN_MENU_MIN_HEIGHT = 150;
const DROPDOWN_MENU_MIN_HEIGHT_CAN_RENDER_ABOVE = 200;

function Container({
  children,
  maxHeight = 600,
  containerRef: _containerRef,
  dropdownRef,
  extraDropdownRef,
  orientRef,
  transitionState,
  fullWidth,
  minWidth = 0,
  innerClassName,
  onTransitionEnd,
  allowRenderAbove = true,
}: {
  readonly children: React.ReactNode;
  readonly containerRef: React.RefObject<HTMLElement>;
  readonly dropdownRef: React.RefObject<HTMLDivElement>;
  readonly extraDropdownRef?: React.RefObject<HTMLDivElement>;
  readonly orientRef?: React.RefObject<HTMLElement>;
  readonly maxHeight?: number;
  readonly allowRenderAbove?: boolean;
  readonly transitionState?: TransitionStatus;
  readonly fullWidth?: boolean;
  readonly minWidth?: number;
  readonly innerClassName?: string;
  readonly onTransitionEnd?: (newOpenValue: boolean) => void;
}) {
  const containerRef = orientRef ?? _containerRef;
  const innerRef = React.useRef<HTMLDivElement | null>(null);

  /** measure the viewport, dropdown menu height and container element to determine the
  position (top or bottom) and height of the dropdown menu
  though the APIs are somewhat regularly used, mdn has a nice visual reference to help
  understand the measurements we're doing: https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect **/

  const getPosition = React.useCallback(() => {
    const {
      x: _x,
      y: _y,
      width: _width,
      height: _height,
    } = containerRef.current?.getBoundingClientRect() ?? {};

    const y = _y ?? 0;
    const x = _x ?? 0;
    const width = _width ?? 0;
    const height = _height ?? 0;

    const screenH = window.innerHeight;

    // space between container element and bottom of viewport
    const spaceBelow =
      screenH - y - height - DROPDOWN_MENU_SPACE_BELOW_TO_SUBTRACT;

    if (dropdownRef.current && innerRef.current) {
      // set the menu to the width of the container element if fullWidth prop is passed
      if (fullWidth) {
        dropdownRef.current.style.width = `${Math.max(width, minWidth)}px`;
      } else {
        // maybe it shouldnt cap the width in cases like DatePicker?
        // dropdownRef.current.style.maxWidth = `${width}px`;
      }

      if (dropdownRef.current.offsetWidth + x < window.innerWidth) {
        // set the left position to the same as the container element
        dropdownRef.current.style.left = `${x}px`;
      } else {
        // set the right position to the same as the end of the container element
        dropdownRef.current.style.right = `${
          window.innerWidth - (x + (_width ?? 0))
        }px`;
      }

      // Set position to render below by default
      dropdownRef.current.style.top = `${y + height}px`;

      // set dropdown menu min height to prevent small scroll container
      innerRef.current.style.minHeight = `${Math.min(
        allowRenderAbove
          ? DROPDOWN_MENU_MIN_HEIGHT_CAN_RENDER_ABOVE
          : DROPDOWN_MENU_MIN_HEIGHT,
        innerRef.current.offsetHeight
      )}px`;

      // determine the actual maximum height from the scroll container and maxHeight prop
      const calculatedMaxHeight = Math.min(
        maxHeight,
        innerRef.current.scrollHeight
      );

      const calculatedMaxHeightFitsBelow =
        spaceBelow > calculatedMaxHeight + DROPDOWN_MENU_BOTTOM_BUFFER_HEIGHT;

      const renderAboveMaxHeight = y - DROPDOWN_MENU_BOTTOM_BUFFER_HEIGHT;

      const renderBelowMaxHeight =
        spaceBelow - DROPDOWN_MENU_BOTTOM_BUFFER_HEIGHT;

      const renderAbove =
        allowRenderAbove && !calculatedMaxHeightFitsBelow && y > spaceBelow;

      // set the max height to make sure the menu fits
      innerRef.current.style.maxHeight = `${Math.min(
        renderAbove ? renderAboveMaxHeight : renderBelowMaxHeight,
        maxHeight
      )}px`;

      // get the height of the outer ref after setting min/max height on inner ref
      const rootH = dropdownRef.current?.offsetHeight ?? 0;

      if (renderAbove) {
        dropdownRef.current.style.top = `${y - rootH}px`;
      }
    }
  }, [
    allowRenderAbove,
    containerRef,
    dropdownRef,
    fullWidth,
    maxHeight,
    minWidth,
  ]);

  const mergedDropdownRef = useMergedRef(dropdownRef, extraDropdownRef ?? null);

  React.useEffect(() => {
    window.addEventListener("wheel", getPosition, { capture: true });
    window.addEventListener("resize", getPosition, { capture: true });
    return () => {
      window.removeEventListener("wheel", getPosition, { capture: true });
      window.removeEventListener("resize", getPosition, { capture: true });
    };
  }, [getPosition]);

  React.useEffect(getPosition, [getPosition]);

  return (
    <div
      data-testid="straps-dropdown-wrapper-outer-container"
      className={classNames("fixed z-[1050] transition-opacity", {
        "opacity-100": transitionState === "entered",
        "opacity-0":
          transitionState === "exiting" ||
          transitionState === "exited" ||
          transitionState === "entering" ||
          transitionState === "preEnter",
      })}
      ref={mergedDropdownRef}
      onTransitionEnd={(event) => {
        // prevent from firing on bubbled events from inside the container
        if (event.target === event.currentTarget) {
          onTransitionEnd?.(transitionState === "entered");
        }
      }}
    >
      <InnerContainer
        innerRef={innerRef}
        className={innerClassName}
        maxHeight={maxHeight}
      >
        {children}
      </InnerContainer>
    </div>
  );
}

function InnerContainer({
  innerRef,
  className,
  children,
  maxHeight,
  height,
  onTransitionEnd,
}: {
  readonly innerRef?:
    | React.RefObject<HTMLDivElement>
    | ((instance: HTMLDivElement | null) => void);
  readonly className?: string;
  readonly children: React.ReactNode;
  readonly maxHeight?: number;
  readonly height?: number;
  readonly onTransitionEnd?: () => void;
}) {
  return (
    <div
      ref={innerRef}
      data-testid="straps-dropdown-wrapper-inner-container"
      className={classNames(
        "flex flex-col overflow-auto bg-pure-white shadow-lg",
        className
      )}
      style={{
        maxHeight,
        height,
      }}
      onTransitionEnd={onTransitionEnd}
    >
      {children}
    </div>
  );
}
